import React, { useState } from 'react';
import { Check, Copy, ZoomIn } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { useAuthenticatedImageSrc } from '../../utils/authenticatedBlobImage';
import { AiGeneratedFigureFrame } from './AiGeneratedFigureFrame';
import { copyRasterImageToClipboard } from './copyRasterImage';

type RasterImagePreviewProps = {
    src?: string | null;
    blobId?: string | null;
    alt: string;
    title?: string;
    showAiWatermark?: boolean;
};

export function RasterImagePreview({
    src,
    blobId,
    alt,
    title,
    showAiWatermark = false,
}: RasterImagePreviewProps) {
    const resolvedSrc = useAuthenticatedImageSrc(typeof src === 'string' ? src : undefined, blobId);
    const [enlargedOpen, setEnlargedOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [copyError, setCopyError] = useState<string | null>(null);

    const handleCopy = async () => {
        if (!resolvedSrc) return;
        setCopyError(null);
        try {
            await copyRasterImageToClipboard(resolvedSrc);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch {
            setCopyError('Could not copy this image.');
        }
    };

    if (!resolvedSrc) return null;

    const dialogTitle = title?.trim() || alt;

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-end gap-1.5">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={() => void handleCopy()}
                    aria-label={`Copy ${dialogTitle}`}
                >
                    {copied ? (
                        <Check className="h-3.5 w-3.5 text-confirm-action" />
                    ) : (
                        <Copy className="h-3.5 w-3.5" />
                    )}
                    {copied ? 'Copied' : 'Copy'}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={() => setEnlargedOpen(true)}
                    aria-label={`Enlarge ${dialogTitle}`}
                >
                    <ZoomIn className="h-3.5 w-3.5" />
                    Enlarge
                </Button>
            </div>
            {copyError ? <p className="text-xs text-destructive-action">{copyError}</p> : null}
            <AiGeneratedFigureFrame show={showAiWatermark} className="block w-full">
                <img src={resolvedSrc} alt={alt} className="max-h-80 w-full rounded-md object-contain" />
            </AiGeneratedFigureFrame>
            <Dialog open={enlargedOpen} onOpenChange={setEnlargedOpen}>
                <DialogContent className="max-h-[90vh] max-w-5xl overflow-auto">
                    <DialogHeader>
                        <DialogTitle>{dialogTitle}</DialogTitle>
                    </DialogHeader>
                    <AiGeneratedFigureFrame show={showAiWatermark} className="block w-full">
                        <img
                            src={resolvedSrc}
                            alt={alt}
                            className="max-h-[80vh] w-full object-contain"
                        />
                    </AiGeneratedFigureFrame>
                </DialogContent>
            </Dialog>
        </div>
    );
}
